/**
 * Exposes the app-level reason dialog to download surfaces.
 *
 * 1. Assign a stable id to this hook caller.
 * 2. Send requests to the provider.
 * 3. Close this caller's request when its component unmounts.
 * The provider and promise lifecycle are in `DownloadReasonDialogProvider.tsx`.
 */

import { useCallback, useContext, useEffect, useMemo } from 'react';

import DownloadReasonDialogContext from './DownloadReasonDialogContext';
import type { RequestDownload } from './types';

/** Supplies unique ids for caller-specific unmount cleanup. */
let idCounter = 0;

/** Gives each hook caller an id so its unmount closes only its own request. */
const useDownloadReasonDialogId = (): string => {
  const id = useMemo(() => idCounter++, []);
  return `download-reason-dialog-${id}`;
};

/** Opens the app-level reason dialog and closes it when this caller unmounts. */
export const useDownloadReasonDialog = (): RequestDownload => {
  const parentId = useDownloadReasonDialogId();
  const { requestBase, closeOnParentUnmount } = useContext(DownloadReasonDialogContext);

  const requestDownload = useCallback<RequestDownload>(
    (request) => requestBase(parentId, request),
    [parentId, requestBase]
  );

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect registers cleanup for the caller's active dialog request.
  useEffect(() => {
    return () => closeOnParentUnmount(parentId);
  }, [closeOnParentUnmount, parentId]);

  return requestDownload;
};
